import { DOCUMENT, Inject, Injectable, LOCALE_ID, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { AccountMember } from '@interfaces/common/accountMember';
import { SegmentRelationship } from '@interfaces/customer/segmentRelationship';
import { User } from '@interfaces/user/user';
import { filter, map, startWith, switchMap } from 'rxjs/operators';

import { CustomerService } from './customer.service';
import { NavService } from './nav.service';
import { UserService } from './user.service';
import { createSimpleHash } from './utils';

export interface PageViewEvent {
  event: string;
  page_title: string;
  url: string;
}
export interface DataEvent {
  business_area: string;
  business_segment: string;
  category: string;
  custom_user_id: string;
  customer_id: string;
  customer_service_specialist: string;
  event: string;
  last_login_date: string;
  login_count: number;
  sales_manager: string;
  locale: string;
  custom_event_date: string;
  event_value: number;
}

@Injectable({
  providedIn: 'root',
})
export class MatomoService {
  userService = inject(UserService);
  navService = inject(NavService);
  customerService = inject(CustomerService);
  router = inject(Router);
  route = inject(ActivatedRoute);

  user = toSignal(this.userService.user$);
  customerId = toSignal(this.navService.customerId$);

  accountMembers = toSignal(
    this.navService.customerId$.pipe(
      switchMap((customerId) => this.customerService.getAccountMembers(customerId)),
    ),
  );

  segmentRelationships = toSignal(
    this.navService.customerId$.pipe(
      switchMap((customerId) => this.customerService.getCurrentSegmentRelationships(customerId)),
    ),
  );

  url = toSignal(
    this.router.events?.pipe(
      startWith(this.route),
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
  );

  pageTitle = toSignal(
    this.router.events?.pipe(
      startWith(this.route),
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.route.snapshot.firstChild?.title || ''),
    ),
  );

  private window: Window | null;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(LOCALE_ID) public locale: string,
  ) {
    this.window = this.document.defaultView;
    this.startTracking();
  }

  startTracking() {
    // Do not track in dev track
    if (this.window?.location.href.includes('localhost')) return;

    // Init Matomo
    (this.window as any)._mtm = (this.window as any)._mtm || [];

    effect(() => {
      const segmentRelationships = this.segmentRelationships();
      const user = this.user();
      const accountMembers = this.accountMembers();
      const url = this.url();
      const title = this.pageTitle();
      const locale = this.locale;
      const customerId = segmentRelationships?.[0]?.customerId || '';

      // Wait for all signals to be ready
      if (!segmentRelationships || !user || !accountMembers || !url) return;

      const pageview = this.buildPageViewEvent({ title: title, url: url });

      const datatopush = this.buildDataEvent({
        accountMembers,
        businessAreas: this.getBusinessAreas(segmentRelationships),
        businessSegments: this.getBusinessSegments(segmentRelationships),
        locale,
        user,
        customerId,
      });

      this.pushDataToMatomo(pageview, datatopush);
    });
  }

  buildDataEvent({
    accountMembers,
    businessAreas,
    businessSegments,
    locale,
    user,
    customerId,
  }: {
    accountMembers: AccountMember[] | null;
    businessAreas: string;
    businessSegments: string;
    locale: string;
    user: User | null | undefined;
    customerId: string;
  }): DataEvent {
    const dataEvent: DataEvent = {
      business_area: businessAreas,
      business_segment: businessSegments,
      category: user && user?.id?.indexOf('auth0') > -1 ? 'Customer' : 'Billerud',
      custom_user_id: createSimpleHash(user?.id).toString(),
      customer_id: customerId,
      customer_service_specialist: this.getAccountMemberId(
        accountMembers,
        'CustomerServiceSpecialist',
      ),
      event: 'eventParams',
      last_login_date: user?.lastLogin || '',
      locale: locale,
      login_count: user?.loginsCount || 0,
      sales_manager: this.getAccountMemberId(accountMembers, 'SalesManager'),
      custom_event_date: new Date().toISOString().slice(0, 10),
      event_value: 1,
    };
    return dataEvent;
  }

  buildPageViewEvent({
    title,
    url,
  }: {
    title: string | null | undefined;
    url: string | null | undefined;
  }) {
    const pageViewEvent: PageViewEvent = {
      event: 'pageView',
      page_title: title || '',
      url: url || '',
    };
    return pageViewEvent;
  }

  pushDataToMatomo(pageview: PageViewEvent, datatopush: DataEvent) {
    if (!this.window) return;

    (this.window as any)._mtm.push(datatopush, pageview);
  }

  // Helper functions

  getBusinessAreas(segmentRelationships: SegmentRelationship[] | null | undefined): string {
    return segmentRelationships?.map((segment) => segment.businessArea).join(',') || 'not-set';
  }

  getBusinessSegments(segmentRelationships: SegmentRelationship[] | null | undefined): string {
    return (
      segmentRelationships?.map((segment) => segment.businessSegment?.name).join(',') || 'not-set'
    );
  }

  getAccountMemberId(
    accountMembers: AccountMember[] | null,
    role: 'CustomerServiceSpecialist' | 'SalesManager',
  ): string {
    if (!accountMembers || accountMembers.length === 0) return 'no-account-members-found';
    const salesManager = accountMembers?.filter(
      (accountMember) => accountMember.isActive && accountMember.role === role,
    );
    return salesManager?.[0]?.recordId ?? `${role}-not-fond`;
  }
}
